"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  CheckSquare,
  FolderKanban,
  Brain,
  FileText,
  Building2,
  ClipboardList,
  Menu,
  Plus,
  X,
  Lightbulb,
  LogOut,
  Shield,
  TrendingUp,
  Users,
  MessageSquare,
  Cog,
  Package,
  Zap,
  BarChart3,
  Target,
  Palette,
  Crosshair,
  BookOpen,
  Wrench,
  LineChart,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { NewItemModal } from "@/components/NewItemModal";
import { ClientSwitcher } from "@/components/ClientSwitcher";
import { useClientContext } from "@/context/ClientContext";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

type NavItem = {
  href: string;
  label: string;
  icon: typeof CheckSquare;
  comingSoon?: boolean;
  /** Hidden from client users, even in sections they can otherwise see. */
  agencyOnly?: boolean;
};

type NavSection = {
  label: string;
  items: NavItem[];
  roleGated?: boolean;
  /** Shown in every context — agency view and client context alike. */
  alwaysVisible?: boolean;
};

const growthOsItems: NavItem[] = [
  { href: "/growth-os/marketing-assets", label: "Marketing Asset Inventory", icon: Package },
  { href: "/growth-os/value-ladder", label: "Value Ladder Canvas", icon: TrendingUp },
  { href: "/growth-os/cvj-diagnostic", label: "CVJ Diagnostic", icon: Crosshair },
  { href: "/growth-os/ideal-client", label: "Ideal Client Profiler", icon: Users },
  { href: "/growth-os/core-message", label: "Core Message Canvas", icon: MessageSquare },
  { href: "/growth-os/value-engine", label: "Value Engine Planner", icon: Cog },
  { href: "/growth-os/growth-engine", label: "Growth Engine", icon: Zap },
  { href: "/growth-os/growth-scorecard", label: "Growth Scorecard", icon: BarChart3 },
  { href: "/growth-os/growth-ideas", label: "Growth Idea Sheet", icon: Lightbulb },
  { href: "/growth-os/big-3", label: "Big 3", icon: Target },
  { href: "/growth-os/brand-kit", label: "Brand Kit", icon: Palette },
];

const workItems: NavItem[] = [
  { href: "/projects", label: "Projects", icon: FolderKanban },
  { href: "/tasks", label: "Tasks", icon: CheckSquare },
  { href: "/tools", label: "Tools", icon: Wrench, agencyOnly: true },
  { href: "/ideas", label: "Ideas", icon: Lightbulb },
  { href: "/documents", label: "Documents", icon: FileText },
];

const helpItems: NavItem[] = [
  { href: "/help", label: "Help & Training", icon: BookOpen },
];

const agencyItems: NavItem[] = [
  { href: "/clients", label: "Clients", icon: Building2 },
  // Cross-client, like Clients and Assessments either side of it: /reporting
  // lists every client on a reporting cadence. It sits in AGENCY rather than
  // WORK because WORK is hidden in Agency View, which is exactly the context
  // this index is for — and because the section's role gate already mirrors
  // the `requireAgencyUser` check on every convex/reporting.ts function.
  { href: "/reporting", label: "Reporting", icon: LineChart },
  { href: "/assessments", label: "Assessments", icon: ClipboardList },
  { href: "/office", label: "Office", icon: Building2 },
  { href: "/memory", label: "Memory", icon: Brain },
  { href: "/admin", label: "Admin", icon: Shield },
];

function UserFooter() {
  const { signOut } = useAuthActions();
  const currentUser = useQuery(api.ideas.currentUser);

  const displayName = currentUser?.name ?? currentUser?.email ?? "You";
  const initials = displayName
    .split(" ")
    .map((p: string) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex items-center gap-2 border-t border-sidebar-border px-4 py-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sidebar-foreground/15 text-xs font-semibold text-sidebar-foreground">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium leading-tight text-sidebar-foreground">{displayName}</div>
        {currentUser?.role && (
          <div className="text-[10px] leading-tight text-sidebar-foreground/60 capitalize">
            {currentUser.role}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => void signOut()}
          title="Sign out"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function SidebarContent({
  onNavigate,
  onOpenNew,
  onClose,
}: {
  onNavigate?: () => void;
  onOpenNew: () => void;
  onClose?: () => void;
}) {
  const pathname = usePathname();
  const currentUser = useQuery(api.ideas.currentUser);

  const { isAgencyView, isClientUser, brandLogoUrl } = useClientContext();

  const RISDALL_LOGO_URL =
    "https://www.risdall.com/wp-content/uploads/2022/09/risdall-logo.svg";
  const logoSrc = brandLogoUrl || RISDALL_LOGO_URL;

  const role = currentUser?.role as string | undefined;
  const isAgencyUser = role === "admin" || role === "risdall";

  const sections: NavSection[] = [
    { label: "GROWTH OS", items: growthOsItems },
    { label: "WORK", items: workItems },
    { label: "AGENCY", items: agencyItems, roleGated: true },
    { label: "HELP", items: helpItems, alwaysVisible: true },
  ];

  return (
    <>
      <div className="border-b border-sidebar-border">
        {/* Branding strip */}
        <div className="flex items-center px-4 pt-4 pb-1">
          <div className="flex flex-1 justify-center">
            <img
              src={logoSrc}
              alt="Logo"
              className="mx-auto block w-auto object-contain"
              style={{
                maxHeight: 28,
                ...(!brandLogoUrl || brandLogoUrl.includes("risdall.com")
                  ? { filter: "brightness(0) invert(1)" }
                  : {}),
              }}
            />
          </div>
          {onClose && (
            <button
              type="button"
              aria-label="Close navigation"
              onClick={onClose}
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-sidebar-border text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Client switcher */}
        <ClientSwitcher />
        <div className="h-2" />
      </div>

      <div className="px-3 pt-4">
        <button
          type="button"
          onClick={onOpenNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-foreground shadow-sm transition-all hover:brightness-105 hover:shadow"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => {
          // HELP: always available, whatever context you're in
          if (!section.alwaysVisible) {
            // AGENCY: only visible when isAgencyView AND user has agency role
            if (section.roleGated && (!isAgencyUser || !isAgencyView)) return null;
            // GROWTH OS + WORK: only visible when a client is selected (not agency view)
            if (!section.roleGated && isAgencyView) return null;
          }

          const items = section.items.filter(
            (item) => !item.agencyOnly || !isClientUser,
          );
          if (items.length === 0) return null;

          return (
            <div key={section.label} className="mt-4 first:mt-0">
              <div className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/70">
                {section.label}
              </div>
              <div className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    item.href === "/"
                      ? pathname === "/"
                      : pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={onNavigate}
                      className={cn(
                        "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[15px] transition-colors md:rounded-lg md:py-2 md:text-sm",
                        isActive
                          ? "border-sidebar-foreground/10 bg-sidebar-foreground/15 text-sidebar-foreground font-semibold shadow-sm"
                          : "border-transparent text-sidebar-foreground/90 font-semibold hover:border-sidebar-border hover:bg-sidebar-accent/80 hover:text-sidebar-foreground",
                        item.comingSoon && !isActive && "text-sidebar-foreground/60 font-medium",
                      )}
                    >
                      <Icon
                        className={cn(
                          "w-4 h-4 flex-shrink-0",
                          isActive ? "text-sidebar-foreground" : "text-sidebar-foreground/80",
                          item.comingSoon && !isActive && "text-sidebar-foreground/50",
                        )}
                      />
                      {item.label}
                      {item.comingSoon && (
                        <span className="ml-auto text-[9px] font-medium uppercase tracking-wide bg-sidebar-foreground/10 text-sidebar-foreground/60 rounded px-1 py-0.5">
                          Soon
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <UserFooter />
    </>
  );
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const { brandLogoUrl } = useClientContext();

  const RISDALL_LOGO_URL =
    "https://www.risdall.com/wp-content/uploads/2022/09/risdall-logo.svg";
  const mobileLogoSrc = brandLogoUrl || RISDALL_LOGO_URL;

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-sidebar-border bg-sidebar px-4 py-3 md:hidden">
        <Link href="/" className="flex min-w-0 items-center gap-3">
          <img
            src={mobileLogoSrc}
            alt="Logo"
            className="w-auto object-contain"
            style={{
              maxHeight: 24,
              ...(!brandLogoUrl || brandLogoUrl.includes("risdall.com")
                ? { filter: "brightness(0) invert(1)" }
                : {}),
            }}
          />
          <span className="text-xs font-medium text-sidebar-foreground/80">
            Agency OS
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMobileOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-sidebar-border text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <aside className="fixed left-0 top-0 z-40 hidden h-full w-60 flex-col border-r border-sidebar-border bg-sidebar/96 text-sidebar-foreground shadow-[1px_0_0_rgba(0,0,0,0.02)] backdrop-blur-sm md:flex">
        <SidebarContent onOpenNew={() => setNewOpen(true)} />
      </aside>

      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] transition-opacity md:hidden",
          mobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={() => setMobileOpen(false)}
      />

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[86vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <SidebarContent
          onClose={() => setMobileOpen(false)}
          onNavigate={() => setMobileOpen(false)}
          onOpenNew={() => {
            setMobileOpen(false);
            setNewOpen(true);
          }}
        />
      </aside>

      <NewItemModal open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}
